/**
 * Salary Negotiation Data
 * Career-specific negotiation strategies, scripts, and market data
 */

import { CareerPath } from './careerPaths';

export interface SalaryRange {
  entry: { min: number; max: number };
  mid: { min: number; max: number };
  senior: { min: number; max: number };
}

export interface NegotiationScript {
  scenario: string;
  context: string;
  script: string;
  tips: string[];
}

export interface NegotiationTip {
  category: 'research' | 'timing' | 'framing' | 'counter' | 'benefits';
  title: string;
  content: string;
  example?: string;
}

export interface SalaryNegotiationData {
  marketRates: SalaryRange;
  negotiationScripts: NegotiationScript[];
  tips: NegotiationTip[];
  benefitsToNegotiate: {
    benefit: string;
    typicalValue: string;
    negotiability: 'high' | 'medium' | 'low';
  }[];
  industryInsights: string[];
  commonMistakes: string[];
  leveragePoints: string[];
}

// Career-specific salary negotiation data
const SALARY_NEGOTIATION_DATA: Record<string, SalaryNegotiationData> = {
  'software-developer': {
    marketRates: {
      entry: { min: 65000, max: 95000 },
      mid: { min: 95000, max: 140000 },
      senior: { min: 140000, max: 200000 },
    },
    negotiationScripts: [
      {
        scenario: 'Initial Offer Response',
        context: 'You\'ve received an offer below your target',
        script: "Thank you for the offer - I'm excited about this opportunity. Based on my research of market rates for this role in [city], and my [X years] of experience with [specific skills], I was expecting compensation in the range of $[target] to $[target+15%]. Is there flexibility to get closer to that range?",
        tips: [
          'Always express enthusiasm first',
          'Cite specific research and qualifications',
          'Give a range, not a single number',
          'End with an open question'
        ]
      },
      {
        scenario: 'Countering a Low Initial Offer',
        context: 'The initial offer is significantly below market rate',
        script: "I appreciate the offer and I\'m very interested in joining the team. I want to be transparent - I\'ve done extensive market research and have competing offers in the $[X] range. Given my expertise in [key skills] and the impact I can make, I\'m looking for $[target]. What can we do to bridge this gap?",
        tips: [
          'Mention competing offers if you have them',
          'Focus on the value you bring',
          'Be specific about your target',
          'Ask them to problem-solve with you'
        ]
      },
      {
        scenario: 'Negotiating Signing Bonus',
        context: 'Base salary is firm but you want additional compensation',
        script: "I understand the base salary has constraints. Would the company consider a signing bonus of $[X] to help bridge the gap? This would reflect my immediate contributions and the opportunity cost of leaving my current role.",
        tips: [
          'Signing bonuses are often more flexible than base',
          'Frame it as a one-time cost vs ongoing expense',
          'Have a specific number ready',
          'Combine with equity or benefits if possible'
        ]
      }
    ],
    tips: [
      {
        category: 'research',
        title: 'Know Your Stack\'s Market Value',
        content: 'Salaries vary dramatically by tech stack. Check levels.fyi, Glassdoor, and Blind for role-specific data. Senior React developers may command 20%+ more than generalists.',
        example: 'A senior React developer in SF: $180-220K vs general full-stack: $150-180K'
      },
      {
        category: 'timing',
        title: 'Negotiate After the Technical Interview',
        content: 'Wait until after you\'ve impressed them technically. Once they\'re invested in hiring you specifically, you have maximum leverage.',
      },
      {
        category: 'framing',
        title: 'Quantify Your Impact',
        content: 'Engineers who can show business impact command higher salaries. Track metrics: performance improvements, revenue impact, team efficiency gains.',
        example: '"My optimization reduced load time by 40%, increasing conversion by $2M/year"'
      },
      {
        category: 'counter',
        title: 'Negotiate Total Compensation',
        content: 'Don\'t just focus on base salary. RSUs, signing bonus, annual bonus target, and WFH stipend can add 20-40% to total comp at top companies.',
      },
      {
        category: 'benefits',
        title: 'Remote Work Has Dollar Value',
        content: 'Full remote can be worth $15-30K in commute savings, time, and quality of life. Factor this into compensation comparisons.',
      }
    ],
    benefitsToNegotiate: [
      { benefit: 'Signing Bonus', typicalValue: '$10,000 - $50,000', negotiability: 'high' },
      { benefit: 'RSU Refresh Grants', typicalValue: '$20,000 - $100,000/year', negotiability: 'medium' },
      { benefit: 'Remote Work', typicalValue: 'Full / Hybrid options', negotiability: 'high' },
      { benefit: 'Annual Bonus Target', typicalValue: '10-20% of base', negotiability: 'medium' },
      { benefit: 'Professional Development', typicalValue: '$2,000 - $10,000/year', negotiability: 'high' },
      { benefit: 'PTO Days', typicalValue: '15-25 days', negotiability: 'medium' },
    ],
    industryInsights: [
      'FAANG/Big Tech pays 50-100% more than average but demands more',
      'Startups may offer lower base but higher equity upside',
      'Fintech and healthcare tech often pay premiums for compliance experience',
      'Remote roles from high-cost-of-living companies offer salary arbitrage opportunity'
    ],
    commonMistakes: [
      'Giving a number first without knowing their budget',
      'Accepting the first offer without negotiating',
      'Focusing only on base salary, missing equity and benefits',
      'Not getting the offer in writing before accepting verbally'
    ],
    leveragePoints: [
      'Competing offers from other companies',
      'Specialized skills in high-demand technologies',
      'Previous experience in their industry/domain',
      'Ability to start immediately vs long notice period'
    ]
  },

  'project-manager': {
    marketRates: {
      entry: { min: 55000, max: 75000 },
      mid: { min: 75000, max: 110000 },
      senior: { min: 110000, max: 160000 },
    },
    negotiationScripts: [
      {
        scenario: 'Highlighting Certification Value',
        context: 'Using PMP or similar certification as leverage',
        script: "My PMP certification and [X] years managing $[amount] portfolios make me uniquely qualified for this role. Based on PMI salary surveys showing certified PMs earn 20% more, I'm targeting a salary of $[X]. How can we work together to reach that?",
        tips: [
          'PMPs typically earn 20-25% more than non-certified',
          'Quantify the portfolio size you\'ve managed',
          'Reference official PMI salary data',
          'Connect certification to direct value'
        ]
      },
      {
        scenario: 'Demonstrating ROI',
        context: 'Showing the value you bring to projects',
        script: "In my last role, I delivered projects 15% under budget and consistently hit deadlines, saving the company an estimated $[X]. This track record of efficiency is why I\'m asking for compensation that reflects this value - specifically $[target].",
        tips: [
          'PMs who demonstrate ROI have strong negotiating positions',
          'Quantify budget savings and efficiency gains',
          'Frame yourself as a profit center, not cost center'
        ]
      }
    ],
    tips: [
      {
        category: 'research',
        title: 'Industry Premium Matters',
        content: 'PM salaries vary by industry. Tech PMs often earn 30-50% more than construction or manufacturing PMs with similar experience.',
        example: 'Tech PM in Seattle: $120-160K vs Manufacturing PM: $80-110K'
      },
      {
        category: 'framing',
        title: 'Speak in Outcomes',
        content: 'Don\'t talk about tasks managed - talk about value delivered. "I led a team of 12" vs "I delivered $2M in revenue-generating features on time and under budget."',
      },
      {
        category: 'benefits',
        title: 'Negotiate Flexibility',
        content: 'PMs often have leverage for flexible schedules since much of the work is coordination and communication which can be done remotely.',
      }
    ],
    benefitsToNegotiate: [
      { benefit: 'Annual Bonus', typicalValue: '10-25% of base', negotiability: 'high' },
      { benefit: 'Certification Reimbursement', typicalValue: '$2,000 - $5,000', negotiability: 'high' },
      { benefit: 'Remote/Hybrid Work', typicalValue: '2-5 days/week', negotiability: 'high' },
      { benefit: 'Professional Development', typicalValue: '$3,000 - $8,000/year', negotiability: 'high' },
    ],
    industryInsights: [
      'Tech industry pays highest for PM roles',
      'Agile/Scrum certifications increase earning potential',
      'Program Managers earn 20-30% more than Project Managers',
      'Contract/consulting PM roles pay 20-40% premium for flexibility'
    ],
    commonMistakes: [
      'Underselling soft skills - they\'re highly valuable',
      'Not quantifying project outcomes',
      'Accepting titles without corresponding pay bumps',
      'Not negotiating performance bonus structure'
    ],
    leveragePoints: [
      'PMP, CSM, or other certifications',
      'Track record of on-time, under-budget delivery',
      'Experience with specific methodologies (Agile, SAFe)',
      'Industry-specific domain knowledge'
    ]
  },

  'data-analyst': {
    marketRates: {
      entry: { min: 55000, max: 75000 },
      mid: { min: 75000, max: 105000 },
      senior: { min: 105000, max: 145000 },
    },
    negotiationScripts: [
      {
        scenario: 'Highlighting Technical Skills',
        context: 'Leveraging Python/SQL expertise',
        script: "My combination of statistical analysis skills and Python/SQL proficiency allows me to handle end-to-end analytics that typically requires multiple roles. Based on my research, analysts with this skillset command $[X] in this market. I\'d like to discuss reaching that range.",
        tips: [
          'Technical analysts are worth more than Excel-only analysts',
          'Highlight end-to-end capabilities',
          'Mention specific tools and languages',
          'Show you reduce headcount needs'
        ]
      },
      {
        scenario: 'Demonstrating Business Impact',
        context: 'Showing how your analysis drives decisions',
        script: "At my previous company, my churn analysis identified $500K in at-risk revenue, and my recommendations reduced churn by 15%. I\'m looking for compensation that reflects my ability to generate this kind of business impact - specifically in the $[X] range.",
        tips: [
          'Always tie analysis to business outcomes',
          'Quantify the dollar impact when possible',
          'Show you understand business context, not just data'
        ]
      }
    ],
    tips: [
      {
        category: 'research',
        title: 'Data Scientist vs Analyst Gap',
        content: 'The line is blurring. If you have ML/statistical modeling skills, you may be able to negotiate data scientist-level pay (often 30-50% higher).',
      },
      {
        category: 'framing',
        title: 'Translate Insights to Dollars',
        content: 'Always connect your analysis to revenue, cost savings, or efficiency. "Identified customer segment worth $2M" hits harder than "segmented customers."',
      },
      {
        category: 'benefits',
        title: 'Learning Stipends Matter',
        content: 'Data tools evolve fast. Negotiate for conference attendance, online course budgets, or cloud computing credits for learning.',
      }
    ],
    benefitsToNegotiate: [
      { benefit: 'Learning/Training Budget', typicalValue: '$2,000 - $5,000/year', negotiability: 'high' },
      { benefit: 'Conference Attendance', typicalValue: '1-2 per year', negotiability: 'high' },
      { benefit: 'Remote Work', typicalValue: 'Full or hybrid', negotiability: 'high' },
      { benefit: 'Tools Access', typicalValue: 'Tableau, cloud credits', negotiability: 'medium' },
    ],
    industryInsights: [
      'Finance and tech pay 20-40% more for analysts',
      'Healthcare analytics is growing rapidly with premium pay',
      'Analysts with visualization skills (Tableau, Power BI) command more',
      'SQL + Python combo is the most valuable skill pairing'
    ],
    commonMistakes: [
      'Not showcasing business impact of analyses',
      'Undervaluing visualization and communication skills',
      'Accepting "Analyst" title when doing "Scientist" work',
      'Not negotiating for tools and learning budgets'
    ],
    leveragePoints: [
      'Python/R statistical programming',
      'SQL expertise and database knowledge',
      'Visualization certifications (Tableau, Power BI)',
      'Industry-specific experience (finance, healthcare, marketing)'
    ]
  }
};

// Generic salary negotiation data
const GENERIC_SALARY_NEGOTIATION: SalaryNegotiationData = {
  marketRates: {
    entry: { min: 45000, max: 65000 },
    mid: { min: 65000, max: 95000 },
    senior: { min: 95000, max: 140000 },
  },
  negotiationScripts: [
    {
      scenario: 'Initial Offer Response',
      context: 'Responding to a first offer',
      script: "Thank you for this offer - I'm genuinely excited about the opportunity to join [company]. Based on my research of market rates and my [X years] of experience in [field], I was hoping we could discuss a salary closer to $[target]. Is there room to move in that direction?",
      tips: [
        'Always express genuine enthusiasm',
        'Back up your ask with research',
        'Ask open-ended questions',
        'Leave room for discussion'
      ]
    },
    {
      scenario: 'When They Ask Your Requirements',
      context: 'Avoiding giving a number first',
      script: "I'm flexible on compensation and more focused on finding the right fit. Could you share the range budgeted for this role? That way I can tell you if we're aligned.",
      tips: [
        'Try to get their range first',
        'Don\'t give a number you can\'t take back',
        'If pressed, give a wide range based on research',
        'Research market rates on Glassdoor, Levels.fyi, Payscale'
      ]
    },
    {
      scenario: 'Negotiating Beyond Base Salary',
      context: 'When base salary is firm',
      script: "I understand the base salary has constraints. I\'d like to explore other ways to reach a package that works for both of us. Could we discuss [signing bonus / additional PTO / remote work / professional development budget]?",
      tips: [
        'Benefits often have more flexibility than base',
        'Prioritize what matters most to you',
        'Have specific asks ready',
        'Some benefits save you money (commute, WFH)'
      ]
    }
  ],
  tips: [
    {
      category: 'research',
      title: 'Know Your Market Value',
      content: 'Research salaries on Glassdoor, LinkedIn Salary, PayScale, and industry-specific sources. Know the range for your role, experience level, and location.',
    },
    {
      category: 'timing',
      title: 'Negotiate After the Offer',
      content: 'Wait until you have a formal offer before negotiating. This is when you have the most leverage - they\'ve already decided they want you.',
    },
    {
      category: 'framing',
      title: 'Frame as Problem-Solving',
      content: 'Instead of demanding, collaborate: "How can we get to $X?" or "What would it take to reach that range?" This keeps the conversation positive.',
    },
    {
      category: 'counter',
      title: 'Counter with Confidence',
      content: 'It\'s expected to negotiate. Most employers build in 10-20% negotiation room. A reasonable counter won\'t cost you the offer.',
    },
    {
      category: 'benefits',
      title: 'Total Compensation Matters',
      content: 'Consider the full package: health insurance, 401k match, PTO, remote work, learning budget. These can add 20-40% to base salary value.',
    }
  ],
  benefitsToNegotiate: [
    { benefit: 'Signing Bonus', typicalValue: '$5,000 - $25,000', negotiability: 'high' },
    { benefit: 'Annual Bonus', typicalValue: '5-20% of base', negotiability: 'medium' },
    { benefit: 'Remote/Hybrid Work', typicalValue: 'Flexibility', negotiability: 'high' },
    { benefit: 'PTO/Vacation Days', typicalValue: '15-25 days', negotiability: 'medium' },
    { benefit: 'Professional Development', typicalValue: '$1,500 - $5,000/year', negotiability: 'high' },
    { benefit: 'Start Date', typicalValue: '2-4 weeks flexibility', negotiability: 'high' },
  ],
  industryInsights: [
    'Tech and finance typically pay highest for comparable roles',
    'Startups may offer lower base but equity upside',
    'Geographic location significantly impacts salary ranges',
    'Remote positions may adjust salary based on your location'
  ],
  commonMistakes: [
    'Accepting the first offer without negotiating',
    'Sharing current salary instead of desired salary',
    'Negotiating before receiving a formal offer',
    'Focusing only on base salary, ignoring total compensation',
    'Being too aggressive or ultimatum-focused',
    'Not getting the final offer in writing'
  ],
  leveragePoints: [
    'Competing offers from other companies',
    'Specialized skills or certifications',
    'Relevant industry experience',
    'Strong interview performance and references',
    'Ability to start quickly if needed'
  ]
};

/**
 * Get salary negotiation data for a career
 */
export function getSalaryNegotiationData(career: CareerPath): SalaryNegotiationData {
  const careerId = career.id.toLowerCase();

  // Check for exact match
  if (SALARY_NEGOTIATION_DATA[careerId]) {
    return SALARY_NEGOTIATION_DATA[careerId];
  }

  // Check for partial matches
  for (const key of Object.keys(SALARY_NEGOTIATION_DATA)) {
    if (careerId.includes(key) || key.includes(careerId)) {
      return SALARY_NEGOTIATION_DATA[key];
    }
  }

  // Return generic data with career-specific salary adjustments
  const genericData = { ...GENERIC_SALARY_NEGOTIATION };

  // Adjust market rates based on career salary data if available
  if (career.salary) {
    genericData.marketRates = {
      entry: {
        min: career.salary.entry_min,
        max: career.salary.entry_max
      },
      mid: {
        min: Math.round(career.salary.entry_max * 1.1),
        max: Math.round(career.salary.entry_max * 1.5)
      },
      senior: {
        min: Math.round(career.salary.entry_max * 1.5),
        max: Math.round(career.salary.entry_max * 2.2)
      },
    };
  }

  return genericData;
}

/**
 * Format salary for display
 */
export function formatSalary(amount: number): string {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount.toLocaleString()}`;
}

/**
 * Get negotiability color
 */
export function getNegotiabilityColor(level: 'high' | 'medium' | 'low'): string {
  const colors = {
    high: '#10b981',
    medium: '#f59e0b',
    low: '#ef4444'
  };
  return colors[level];
}
